import { notification } from "antd";
import Axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig } from "axios";
import axiosRetry, { IAxiosRetryConfig } from "axios-retry";
import { API_DOMAIN, MESSAGE, ROUTE_API, STORAGE } from "constant";
import TokenStorageService from "services/storage";
import { StorageUtil } from "./storage";

type RetryConfig = Omit<IAxiosRetryConfig, "retryDelay"> & {
  retryDelay?: number;
};

class ApiV3 {
  private axiosInstance: AxiosInstance;
  readonly defaultHeaders: any = { "Content-Type": "application/json", Accept: "application/json" };
  constructor() {
    let headers: any = this.defaultHeaders;
    headers = { ...headers, ...this.getAuthHeader() };
    const axiosRequestConfig: AxiosRequestConfig = {
      baseURL: API_DOMAIN,
      timeout: 60000,
      headers,
    };
    const axiosInstance: AxiosInstance = Axios.create(axiosRequestConfig);
    axiosRetry(axiosInstance, { retries: 1 });

    this.createAxiosResponseInterceptor(axiosInstance);
    this.axiosInstance = axiosInstance;
  }

  createAxiosResponseInterceptor(axiosInstance: AxiosInstance) {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // return if not have response or not 401 unauthorized
        if (!error.response || (error.response && error.response.status !== 401)) {
          return Promise.reject(error);
        }
        // eject to prevent looping
        axiosInstance.interceptors.response.eject(interceptor);
        // get refreshToken from localStorage
        const refreshToken: String | null = TokenStorageService.getRefreshToken();
        if (!refreshToken) {
          sessionStorage.removeItem(STORAGE.SESSION_CURRENT_DATE);
          localStorage.clear();
          window.location.reload();
          return Promise.reject(null);
        }
        // call api refresh token
        return axiosInstance
          .post(ROUTE_API.AUTH_ROUTE.REFRESH_ACCESS_TOKEN, { refreshToken })
          .then((res) => {
            const { accessToken, refreshToken } = res.data.data;
            TokenStorageService.setToken({ accessToken, refreshToken });
            error.response.config.headers["Authorization"] = "Bearer " + accessToken;
            return axiosInstance(error.response.config);
          })
          .catch((err) => {
            sessionStorage.removeItem(STORAGE.SESSION_CURRENT_DATE);
            localStorage.clear();
            window.location.reload();
            return Promise.reject(err);
          })
          .finally(() => this.createAxiosResponseInterceptor(axiosInstance));
      }
    );
  }

  updateHeader() {
    let headersConfig: any = {
      ...this.defaultHeaders,
      ...this.getAuthHeader(),
    };

    this.axiosInstance.defaults.headers = headersConfig;
  }

  getAuthHeader() {
    const data: any = {};
    let token = StorageUtil.getItem(STORAGE.LOCAL_ACCESS_TOKEN);
    if (token) {
      data.Authorization = "Bearer ".concat(token);
    }

    return { ...data };
  }

  setHeaders() {
    let headersConfig = {
      "Content-Type": "application/json",
    } as any;
    return (this.axiosInstance.defaults.headers = headersConfig);
  }

  handleError(error: any, hiddenServerError?: boolean) {
    if (error) {
      if (error.response) {
        let description: string = error.response.data.message;
        if (!Boolean(hiddenServerError)) {
          notification.error({ message: MESSAGE.ERROR.BASE, description });
        }
        return error.response;
      } else notification.error({ message: MESSAGE.ERROR.BASE, description: MESSAGE.ERROR.NETWORK });
    } else {
      notification.error({
        message: MESSAGE.ERROR.BASE,
        description: MESSAGE.ERROR.NETWORK,
      });
    }
  }

  get<T>(
    input: { url: string; query?: any },
    headers: any = {},
    hiddenServerError?: boolean,
    retryConfig?: RetryConfig
  ): Promise<T | null> {
    return this.send(
      {
        method: "get",
        url: input.url,
        params: input.query || {},
        data: {},
        headers,
      },
      hiddenServerError
    );
  }

  post<T>(
    input: { url: string; data: any; query?: any },
    headers: any = {},
    hiddenServerError?: boolean,
    retryConfig?: RetryConfig
  ): Promise<T | null> {
    return this.send(
      {
        method: "post",
        url: input.url,
        params: input.query || {},
        data: input.data,
        headers,
      },
      hiddenServerError
    );
  }

  put<T>(
    input: { url: string; data?: any; query?: any },
    headers: any = {},
    hiddenServerError?: boolean,
    retryConfig?: RetryConfig
  ): Promise<T | null> {
    return this.send(
      {
        method: "put",
        url: input.url,
        params: input.query || {},
        data: input.data,
        headers,
      },
      hiddenServerError
    );
  }

  delete<T>(
    input: { url: string; data?: any; query?: any },
    headers: any = {},
    hiddenServerError?: boolean,
    retryConfig?: RetryConfig
  ): Promise<T | null> {
    return this.send(
      input.query
        ? { method: "delete", url: input.url, params: input.query, headers, data: input.data }
        : { method: "delete", url: input.url, headers, data: input.data },
      hiddenServerError
    );
  }

  // head(
  //   input: { url: string; query?: any },
  //   headers: any = {},
  //   hiddenServerError?: boolean,
  //   retryConfig?: RetryConfig
  // ): Promise<T | null> {
  //   return this.send(
  //     {
  //       method: "head",
  //       url: input.url,
  //       params: input.query || {},
  //       headers,
  //     },
  //     hiddenServerError
  //   );
  // }

  send<T>(options: AxiosRequestConfig, hiddenServerError?: boolean, retryConfig?: RetryConfig): Promise<T | null> {
    const params: any = {
      ...options,
      headers: {
        ...this.axiosInstance.defaults.headers,
        ...options.headers,
      },
    };

    if (retryConfig) {
      const retryObj: any = { ...retryConfig };
      if (retryConfig.retryDelay) {
        retryObj.retryDelay = () => retryConfig.retryDelay;
      }

      params["axios-retry"] = retryObj;
    }

    // add Authorization to header as defaults
    if (!params.headers.Authorization) {
      this.updateHeader();
      params.headers.Authorization = this.getAuthHeader().Authorization;
    }

    // remove undefined value
    for (const header in params.headers) {
      if (params.headers.hasOwnProperty(header)) {
        if (params.headers[header] === undefined) {
          delete params.headers[header];
        }
      }
    }
    return this.bindData<T>(this.axiosInstance(params));
  }

  bindData<T>(axiosInstance: AxiosPromise): Promise<T | null> {
    return axiosInstance.then((res) => {
      const data = res.data;
      return data;
    });
    // .catch((err) => {
    //   console.log(241, err);
    //   return OtherUtil.loadError(err);
    // });
  }
}
// eslint-disable-next-line import/no-anonymous-default-export
export default new ApiV3();
